<template>
	<div class="contract-card" :class="{ 'contract-card--interactive': interactive }"
		:style="{ 'background-color': cardBackgroundColor, 'color': cardTextColor }" data-cy="contract-card">
		<div class="contract-card-top">
			<v-icon class="mr-1" size="18px">mdi-gavel</v-icon>
			<div class="contract-card-top-name">
				<span class="contract-name" data-cy="contract-name">{{ contractTemplateLabel }}</span>
				<span class="marketplace-name" data-cy="contract-client-name">{{ clientName }}</span>
			</div>
			<div :style="{ 'background-color': cardBackgroundColorChip }" class="contract-card-top-chip" data-cy="contract-status">
				<v-icon size="16px">mdi-draw</v-icon>
				<span data-cy="contract-status">{{ cardStatusText }}</span>
			</div>
		</div>
		<template v-if="!cardIsPhysical">
			<span class="contract-card-info" v-html="cardInfoText" data-cy="contract-info"></span>
			<span class="contract-card-issued" data-cy="contract-issued">
				<template v-if="!cardIsTerminated">{{ issuedOnText }}</template>
				<template v-else data-cy="contract-terminated">{{ $t('text.terminatedOn') }} {{ formatDate(contract.endDate) }}</template>
			</span>
		</template>
		<template v-else>
			<span class="contract-card-issued" data-cy="contract-terminated">
				<template v-if="cardIsTerminated">{{ $t('text.terminatedOn') }} {{ formatDate(contract.endDate) }}</template>
			</span>
		</template>
	</div>
</template>
<script lang="ts">
import { Contract } from '../../../../api2/src/modules/contract/contract.entities'
import ContractStatusMixin from '../../mixins/ContractStatusMixin.vue'
import Common from '../../mixins/Common.vue'

export default {
	name: 'ContractCard',
	mixins: [ Common, ContractStatusMixin ],
	props: {
		contract: {
			type: Object as () => Contract,
			required: true
		},
		clientName: {
			type: String,
			required: false
		},
		interactive: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		cardIsPhysical() {
			return this.isPhysical
		},
		cardIsTerminated() {
			return this.isTerminated
		},
		cardVariant() {
			if (this.cardIsTerminated) return 'terminated'
			if (this.overdue) return 'overdue'
			if (this.toBeSigned) return 'unsigned'
			if (this.isSigned || this.cardIsPhysical) return 'signed'
			return 'unknown'
		},
		cardBackgroundColor() {
			if (this.cardVariant === 'terminated') return '#F4F4F4'
			if (this.cardVariant === 'overdue') return '#FDE0E0'
			if (this.cardVariant === 'unsigned') return '#FFF2D4'
			if (this.cardVariant === 'signed') return '#E5F6DA'
			return '#DBDBDB'
		},
		cardTextColor() {
			if (this.cardVariant === 'terminated') return '#000000'
			if (this.cardVariant === 'overdue') return '#611C1C'
			if (this.cardVariant === 'unsigned') return '#664800'
			if (this.cardVariant === 'signed') return '#28500E'
			return '#DBDBDB'
		},
		cardBackgroundColorChip() {
			if (this.cardVariant === 'terminated') return '#8F8F8F'
			if (this.cardVariant === 'overdue') return '#F34545'
			if (this.cardVariant === 'unsigned') return '#FFB400'
			if (this.cardVariant === 'signed') return '#64C823'
			return '#8F8F8F'
		},
		cardStatusText() {
			if (this.cardVariant === 'terminated') return this.$t('text.terminated')
			if (this.cardVariant === 'signed') return this.$t('text.signed')
			return this.$t('text.unsigned')
		},
		cardInfoText() {
			return this.infoText
		},
		contractTemplateLabel() {
			const nameByLocale = this.contract?.contractTemplate?.name || {}
			const selectedValue = nameByLocale[this.selectedLocale]
			const fallbackValue = nameByLocale[this.defaultLocale.code]
			const selected = typeof selectedValue === 'string' ? selectedValue.trim() : ''
			const fallback = typeof fallbackValue === 'string' ? fallbackValue.trim() : ''
			const firstNamed = Object.values(nameByLocale).find(value => typeof value === 'string' && value.trim()) as string | undefined
			const label = selected
				|| fallback
				|| firstNamed
				|| ''
			if (label) {
				return label
			}
			return 'Contract'
		},
	},
}
</script>

<style scoped lang="scss">
.contract-card {
	padding: 14px;
	border-radius: 4px;
	display: flex;
	flex-direction: column;

	&--interactive {
		cursor: pointer;
		transition: transform 0.2s ease, box-shadow 0.2s ease;

		&:hover {
			transform: translateY(-2px);
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
		}
	}

	&-top {
		display: flex;
		align-items: flex-start;
		gap: 8px;

		&-name {
			display: flex;
			flex-direction: column;
			gap: 2px;
			min-width: 0;
			flex: 1;

			.contract-name {
				font-size: 17px;
				font-weight: 500;
				line-height: 22px;
				letter-spacing: -0.071px;
				display: -webkit-box;
				-webkit-line-clamp: 2;
				-webkit-box-orient: vertical;
				overflow: hidden;
				text-overflow: ellipsis;
				word-break: break-word;
			}

			.marketplace-name {
				font-size: 12px;
				font-weight: 600;
				line-height: 16px;
				letter-spacing: 0.001px;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}

		&-chip {
			flex-shrink: 0;
			display: flex;
			height: 22px;
			padding: 0px 6px;
			justify-content: center;
			align-items: center;
			gap: 4px;
			border-radius: 4px;
			font-size: 12px;
			font-weight: 400;
			line-height: 16px;
			letter-spacing: 0.001px;
			color: white;
			white-space: nowrap;
		}
	}

	&-info {
		padding-top: 12px;
		font-size: 15px;
		font-weight: 400;
		line-height: 20px;
		letter-spacing: -0.037px;
	}

	&-issued {
		text-align: right;
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		letter-spacing: 0.001px;
		color: rgba(0, 0, 0, 0.5)
	}
}
</style>
